import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  BarChart3, PieChart, TrendingUp, TrendingDown,
  Users, Phone, DollarSign, Target, Calendar,
  Download, Filter, RefreshCw, Activity,
  ArrowUp, ArrowDown, Globe, Clock
} from 'lucide-react';

export default function AnalyticsPage() {
  const [stats, setStats] = useState({
    leads: { total: 0, evolution: 0 },
    calls: { total: 0, evolution: 0 },
    deals: { total: 0, montant: 0, evolution: 0 }
  });
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    loadStats();
  }, [period]);

  async function loadStats() {
    const { data: leads } = await supabase.from('leads').select('created_at');
    const { data: calls } = await supabase.from('calls').select('created_at');
    const { data: deals } = await supabase.from('deals').select('amount, created_at');

    setStats({
      leads: { total: leads?.length || 0, evolution: 12 },
      calls: { total: calls?.length || 0, evolution: 8 },
      deals: { total: deals?.length || 0, montant: deals?.reduce((acc, d) => acc + (d.amount || 0), 0) || 0, evolution: 15 }
    });
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
          Analytiques
        </h1>
        <div className="flex gap-2">
          <select className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-2 text-white">
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="quarter">Ce trimestre</option>
            <option value="year">Cette année</option>
          </select>
          <button className="p-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-400 hover:text-white">
            <Download size={20} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
          <div className="flex justify-between items-start">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Users className="text-blue-400" size={24} />
            </div>
            <span className="flex items-center gap-1 text-sm text-green-400">
              <ArrowUp size={16} />
              +{stats.leads.evolution}%
            </span>
          </div>
          <p className="text-2xl font-bold text-white mt-4">{stats.leads.total}</p>
          <p className="text-sm text-gray-400">Leads totaux</p>
        </div>

        <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
          <div className="flex justify-between items-start">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <Phone className="text-green-400" size={24} />
            </div>
            <span className="flex items-center gap-1 text-sm text-green-400">
              <ArrowUp size={16} />
              +{stats.calls.evolution}%
            </span>
          </div>
          <p className="text-2xl font-bold text-white mt-4">{stats.calls.total}</p>
          <p className="text-sm text-gray-400">Appels</p>
        </div>

        <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
          <div className="flex justify-between items-start">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <DollarSign className="text-purple-400" size={24} />
            </div>
            <span className="flex items-center gap-1 text-sm text-green-400">
              <ArrowUp size={16} />
              +{stats.deals.evolution}%
            </span>
          </div>
          <p className="text-2xl font-bold text-white mt-4">{stats.deals.montant.toLocaleString()}€</p>
          <p className="text-sm text-gray-400">CA total</p>
        </div>

        <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
          <div className="flex justify-between items-start">
            <div className="p-3 bg-amber-500/10 rounded-lg">
              <Target className="text-amber-400" size={24} />
            </div>
            <span className="flex items-center gap-1 text-sm text-green-400">
              <ArrowUp size={16} />
              +5%
            </span>
          </div>
          <p className="text-2xl font-bold text-white mt-4">78%</p>
          <p className="text-sm text-gray-400">Taux conversion</p>
        </div>
      </div>

      {/* Graphiques (simulés) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
          <h3 className="text-lg font-semibold text-white mb-4">Évolution des leads</h3>
          <div className="h-64 flex items-end justify-between gap-2">
            {[65, 45, 75, 50, 80, 55, 70].map((height, i) => (
              <div key={i} className="w-full">
                <div className="h-48 bg-gray-800 rounded-t-lg relative">
                  <div className="absolute bottom-0 w-full bg-cyan-500 rounded-t-lg transition-all" style={{ height: `${height}%` }}></div>
                </div>
                <p className="text-center text-sm text-gray-500 mt-2">J{i+1}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
          <h3 className="text-lg font-semibold text-white mb-4">Répartition des deals</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Gagnés</span>
                <span className="text-white">45%</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: '45%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">En cours</span>
                <span className="text-white">30%</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-500 rounded-full" style={{ width: '30%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Perdus</span>
                <span className="text-white">25%</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full" style={{ width: '25%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}